import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { MessageCursor } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { EffectApiContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import {
  createDraftSendStateCollection as createDraftSendStateCollectionCore,
  type DraftSendStateCollection,
} from "@redotech/redo-support-v2/drafts/support-ticket-draft-send-state-collection";
import {
  createDraftsCollection as createDraftsCollectionCore,
  type DraftsCollection,
} from "@redotech/redo-support-v2/drafts/support-ticket-drafts-collection";
import {
  createSupportTicketListCollection as createSupportTicketListCollectionCore,
  type SupportTicketListCollectionWithLoader,
} from "@redotech/redo-support-v2/query/support-ticket-list-collection";
import type { SupportTicketQueryScope } from "@redotech/redo-support-v2/query/support-ticket-query-keys";

import {
  createMessageLinksCollection as createMessageLinksCollectionCore,
  createMessagePagesCollection,
  createMessagesCollection as createMessagesCollectionCore,
  createThreadLinksCollection as createThreadLinksCollectionCore,
  createThreadsCollection as createThreadsCollectionCore,
  createTimelineEventsCollection as createTimelineEventsCollectionCore,
  fetchSupportTicketMessagesThroughLoaded,
  fetchSupportTicketThreads as fetchSupportTicketThreadsCore,
  replaceMessageLinksForSupportTicket,
  replaceThreadLinksForSupportTicket,
  supportTicketTranscriptQueryKeys,
  writeMessageLinks,
  writeMessagePages,
  type MessageLinksCollection,
  type MessagePagesCollection,
  type MessagesCollection,
  type SupportTicketMessagesFetchResult,
  type SupportTicketThreadsFetchResult,
  type ThreadLinksCollection,
  type ThreadsCollection,
  type TimelineEventsCollection,
} from "@redotech/redo-support-v2/query/support-ticket-transcript-collections";
import { fetchEarlierSupportTicketMessages as fetchEarlierSupportTicketMessagesCore } from "@redotech/redo-support-v2/query/support-ticket-transcript-requests";
import {
  createPresenceCollection as createPresenceCollectionCore,
  type PresenceCollection,
} from "@redotech/redo-support-v2/realtime/support-ticket-presence-collection";

import {
  createCustomerEventsCollection as createCustomerEventsCollectionCore,
  createOrdersCollection as createOrdersCollectionCore,
  createReturnsCollection as createReturnsCollectionCore,
  type CustomerEventsCollection,
  type OrdersCollection,
  type ReturnsCollection,
} from "@redotech/redo-support-v2/query/support-ticket-related-records-collections";
import type { QueryClient } from "@tanstack/react-query";
import {
  createMerchantSupportTicketDataSource,
  type MerchantSupportTicketDataSource,
} from "./support-ticket-data-source";

export const transcriptQueryKeys: {
  readonly all: readonly ["supportTickets", "transcript"];
  readonly threads: () => readonly ["supportTickets", "transcript", "threads"];
  readonly messages: () => readonly [
    "supportTickets",
    "transcript",
    "messages",
  ];
  readonly timelineEvents: () => readonly [
    "supportTickets",
    "transcript",
    "timelineEvents",
  ];
} = supportTicketTranscriptQueryKeys;

interface CreateGlobalCollectionsOptions {
  queryClient: QueryClient;
  effectApi: EffectApiContext;
  queryScope?: SupportTicketQueryScope;
}

export async function fetchSupportTicketThreads(
  effectApi: EffectApiContext,
  supportTicketId: SupportTicketId,
): Promise<SupportTicketThreadsFetchResult> {
  return fetchSupportTicketThreadsCore(
    createMerchantSupportTicketDataSource({ effectApi }).transcript,
    supportTicketId,
  );
}

export async function fetchSupportTicketMessages(
  effectApi: EffectApiContext,
  messagePages: MessagePagesCollection,
  supportTicketId: SupportTicketId,
): Promise<SupportTicketMessagesFetchResult> {
  return fetchSupportTicketMessagesThroughLoaded(
    createMerchantSupportTicketDataSource({ effectApi }).transcript,
    messagePages,
    supportTicketId,
  );
}

/** Load one page older than the ticket's transcript reaches and keep it loaded. */
export async function loadEarlierSupportTicketMessages({
  effectApi,
  messages: messagesCollection,
  messageLinks: messageLinksCollection,
  messagePages: messagePagesCollection,
  supportTicketId,
  earlierCursor,
}: {
  effectApi: EffectApiContext;
  messages: MessagesCollection;
  messageLinks: MessageLinksCollection;
  messagePages: MessagePagesCollection;
  supportTicketId: SupportTicketId;
  earlierCursor: MessageCursor;
}): Promise<void> {
  const result = await fetchEarlierSupportTicketMessagesCore(
    createMerchantSupportTicketDataSource({ effectApi }).transcript,
    supportTicketId,
    earlierCursor,
  );
  // Record the new boundary before writing rows, so a refetch keeps them.
  writeMessagePages(messagePagesCollection, supportTicketId, {
    earlierCursor: result.earlierCursor,
    ...(result.loadedThrough ? { loadedThrough: result.loadedThrough } : {}),
  });
  if (messagesCollection.isReady()) {
    messagesCollection.utils.writeBatch(() => {
      for (const message of result.entities) {
        messagesCollection.utils.writeUpsert(message);
      }
    });
  }
  writeMessageLinks(messageLinksCollection, supportTicketId, result.links);
}

/** Pull the ticket's authoritative transcript; replace this ticket's links only. */
export async function reconcileSupportTicketTranscript({
  effectApi,
  threads: threadsCollection,
  threadLinks: threadLinksCollection,
  messages: messagesCollection,
  messageLinks: messageLinksCollection,
  messagePages: messagePagesCollection,
  supportTicketId,
  signal,
}: {
  effectApi: EffectApiContext;
  threads: ThreadsCollection;
  threadLinks: ThreadLinksCollection;
  messages: MessagesCollection;
  messageLinks: MessageLinksCollection;
  messagePages: MessagePagesCollection;
  supportTicketId: SupportTicketId;
  signal?: AbortSignal;
}): Promise<void> {
  const [threadsResult, messagesResult] = await Promise.all([
    fetchSupportTicketThreads(effectApi, supportTicketId),
    fetchSupportTicketMessages(
      effectApi,
      messagePagesCollection,
      supportTicketId,
    ),
  ]);
  if (signal?.aborted) {
    return;
  }

  if (threadsCollection.isReady()) {
    threadsCollection.utils.writeBatch(() => {
      for (const thread of threadsResult.entities) {
        threadsCollection.utils.writeUpsert(thread);
      }
    });
  }
  replaceThreadLinksForSupportTicket(
    threadLinksCollection,
    supportTicketId,
    threadsResult.links,
  );

  if (messagesCollection.isReady()) {
    messagesCollection.utils.writeBatch(() => {
      for (const message of messagesResult.entities) {
        messagesCollection.utils.writeUpsert(message);
      }
    });
  }
  replaceMessageLinksForSupportTicket(
    messageLinksCollection,
    supportTicketId,
    messagesResult.links,
  );
  writeMessagePages(messagePagesCollection, supportTicketId, {
    earlierCursor: messagesResult.earlierCursor,
  });
}

function createThreadsCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  threadLinks: ThreadLinksCollection,
  scope?: SupportTicketQueryScope,
) {
  return createThreadsCollectionCore({
    queryClient,
    api: dataSource.transcript,
    scope,
    threadLinks,
  });
}

function createMessagesCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  messageLinks: MessageLinksCollection,
  messagePages: MessagePagesCollection,
  scope?: SupportTicketQueryScope,
) {
  return createMessagesCollectionCore({
    queryClient,
    api: dataSource.transcript,
    scope,
    messageLinks,
    messagePages,
  });
}

function createTimelineEventsCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketQueryScope,
) {
  return createTimelineEventsCollectionCore({
    queryClient,
    api: dataSource.transcript,
    scope,
  });
}

function createPresenceCollection() {
  return createPresenceCollectionCore();
}

function createOrdersCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketQueryScope,
) {
  return createOrdersCollectionCore({
    queryClient,
    api: dataSource.relatedRecords,
    scope,
  });
}

function createReturnsCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketQueryScope,
) {
  return createReturnsCollectionCore({
    queryClient,
    api: dataSource.relatedRecords,
    scope,
  });
}

function createCustomerEventsCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketQueryScope,
) {
  return createCustomerEventsCollectionCore({
    queryClient,
    api: dataSource.relatedRecords,
    scope,
  });
}

function createDraftsCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
) {
  return createDraftsCollectionCore({ queryClient, api: dataSource.drafts });
}

export type SupportTicketListCollection = SupportTicketListCollectionWithLoader;

export interface GlobalSupportTicketCollections {
  supportTicketsList: SupportTicketListCollection;
  threads: ThreadsCollection;
  threadLinks: ThreadLinksCollection;
  messages: MessagesCollection;
  messageLinks: MessageLinksCollection;
  /** How far back each ticket's transcript is loaded. */
  messagePages: MessagePagesCollection;
  drafts: DraftsCollection;
  /** Client-only overlay for transient draft send-lifecycle (stage/sendAt). */
  draftSendState: DraftSendStateCollection;
  presence: PresenceCollection;
  orders: OrdersCollection;
  returns: ReturnsCollection;
  timelineEvents: TimelineEventsCollection;
  customerEvents: CustomerEventsCollection;
}

export function createGlobalSupportTicketCollections(
  options: CreateGlobalCollectionsOptions,
): GlobalSupportTicketCollections {
  const dataSource = createMerchantSupportTicketDataSource(options);
  const supportTicketsList = createSupportTicketListCollectionCore({
    queryClient: options.queryClient,
    api: dataSource.list,
    scope: options.queryScope,
    onUpdateError: (supportTicketId, err) => {
      console.error("[supportTickets] PATCH failed for", supportTicketId, err);
    },
  });
  const threadLinks = createThreadLinksCollectionCore();
  const messageLinks = createMessageLinksCollectionCore();
  const messagePages = createMessagePagesCollection();
  const threads = createThreadsCollection(
    options.queryClient,
    dataSource,
    threadLinks,
    options.queryScope,
  );
  const messages = createMessagesCollection(
    options.queryClient,
    dataSource,
    messageLinks,
    messagePages,
    options.queryScope,
  );
  const drafts = createDraftsCollection(options.queryClient, dataSource);
  const draftSendState = createDraftSendStateCollectionCore();
  const presence = createPresenceCollection();
  const orders = createOrdersCollection(
    options.queryClient,
    dataSource,
    options.queryScope,
  );
  const returns = createReturnsCollection(
    options.queryClient,
    dataSource,
    options.queryScope,
  );
  const customerEvents = createCustomerEventsCollection(
    options.queryClient,
    dataSource,
    options.queryScope,
  );
  const timelineEvents = createTimelineEventsCollection(
    options.queryClient,
    dataSource,
    options.queryScope,
  );
  return {
    supportTicketsList,
    threads,
    threadLinks,
    messages,
    messageLinks,
    messagePages,
    drafts,
    draftSendState,
    presence,
    orders,
    returns,
    timelineEvents,
    customerEvents,
  };
}

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { type DraftCollectionItem } from "@redotech/redo-support-v2/drafts/support-ticket-draft-requests";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { supportTicketDraftQueryKeys as draftQueryKeys } from "@redotech/redo-support-v2/query/support-ticket-query-keys";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  supportTicketOrderQueryKeys as orderQueryKeys,
  supportTicketReturnQueryKeys as returnQueryKeys,
  type OrderWithPickStatus,
} from "@redotech/redo-support-v2/query/support-ticket-related-records-collections";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { supportTicketTagQueryKeys as tagQueryKeys } from "@redotech/redo-support-v2/query/support-ticket-tags-collections";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { type SupportTicketPresenceRecord } from "@redotech/redo-support-v2/realtime/support-ticket-presence-collection";

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { supportTicketTranscriptQueryKeys as supportTicketTranscriptKeys } from "@redotech/redo-support-v2/query/support-ticket-transcript-collections";
